import { create } from "zustand";
import z from "zod";
import { createSelectors } from "./create-selectors.util";
import { ID } from "@appwrite.io/console";
import { trackAnalyticsEvent } from "@/lib/analytics";
import { imagineStore } from "./imagine.store";
import type {
  AnalyticsEventName,
  AnalyticsEventPayload,
} from "@imagine/analytics/events";
import { errorHandling } from "@/components/chat/error-handling";
import { HttpError } from "@/lib/http-error";

export const configSchema = z.object({
  APPWRITE_ENDPOINT: z.string(),
  AI_SERVICE_ENDPOINT: z.string(),
  REALTIME_URI: z.string(),
  APPWRITE_SITES_BASE_URL: z.string(),
});

export type ImagineTheme = "light" | "dark" | "system";

export type OnErrorCallbackParams = {
  traceId?: string;
  message: string;
  isChatError: boolean;
} | null;

export type ImagineCallbacks = {
  onProjectNameChange?: (name: string) => void;
  onAddDomain?: () => void | Promise<void>;
  onManageDomains?: (primaryDomain?: string) => void | Promise<void>;
  onUpgrade?: () => void | Promise<void>;
  onError?: (params: OnErrorCallbackParams) => void | Promise<void>;
};

type ImagineConfigState = {
  config: z.infer<typeof configSchema> | null;
  initialized: boolean;
  theme: ImagineTheme;
  callbacks: ImagineCallbacks | null;
  consoleSessionId: string;
  constructAppwriteSitesPreviewUrl: (projectId: string) => URL;
};

type ImagineConfigStore = ImagineConfigState & {
  setTheme: (theme: ImagineTheme) => void;
  setError: (error: Error | HttpError | null) => void;
};

const baseStore = create<ImagineConfigStore>((set, get) => ({
  config: null,
  callbacks: null,
  initialized: false,
  theme: (window.localStorage.getItem("theme") as ImagineTheme) || "light",
  consoleSessionId: `nonconsole-${ID.unique()}`,
  constructAppwriteSitesPreviewUrl: (projectId: string) => {
    const config = get().config;
    const appwriteSitesBaseUrl = new URL(config!.APPWRITE_SITES_BASE_URL);
    const protocol = appwriteSitesBaseUrl.protocol;
    const hostname = appwriteSitesBaseUrl.hostname;
    return new URL(`${protocol}//imagine-${projectId}.${hostname}/`);
  },
  setTheme: (theme: ImagineTheme) => {
    let themeToSet: ImagineTheme;
    if (theme === "system") {
      themeToSet = "light";
    } else {
      themeToSet = theme as ImagineTheme;
    }
    set({ theme: themeToSet });
    window.localStorage.setItem("theme", themeToSet);
  },
  setError: (error: Error | HttpError | null) => {
    const callbacks = get().callbacks;

    if (!callbacks?.onError) return;

    if (error === null) {
      callbacks.onError(null);
      return;
    }

    if (error instanceof HttpError) {
      callbacks.onError({
        traceId: error.traceId ?? undefined,
        message: error.message ?? "An unexpected error occurred",
        isChatError: false,
      });
      return;
    }

    if (errorHandling.isImagineChatError(error)) {
      const sanitized = errorHandling.sanitizeChatError(error);
      callbacks.onError({
        traceId: sanitized.traceId,
        message: sanitized.title,
        isChatError: true,
      });
      return;
    }
  },
}));

export const imagineConfigStore = createSelectors(baseStore);

export type InitImagineConfigConfig = z.infer<typeof configSchema>;

export type InitImagineConfigOptions = {
  consoleSessionId: string;
  initialTheme: ImagineTheme;
  callbacks?: ImagineCallbacks;
};

function wrapCallbackWithAnalytics<
  TArgs extends unknown[],
  TReturn,
  TEventName extends AnalyticsEventName,
>(
  callback: ((...args: TArgs) => TReturn) | undefined,
  eventName: TEventName,
  buildPayload: (
    projectId: string | null,
    ...args: TArgs
  ) => AnalyticsEventPayload<TEventName> | null,
  requireProjectId = true,
): ((...args: TArgs) => TReturn) | undefined {
  return ((...args: TArgs) => {
    const projectId = imagineStore.getState().projectId;
    const payload = buildPayload(projectId, ...args);
    if (payload && (!requireProjectId || projectId)) {
      trackAnalyticsEvent(eventName, payload).catch(() => {
        // Silently fail analytics
      });
    }
    return callback?.(...args);
  }) as (...args: TArgs) => TReturn;
}

export type InitImagineConfigFn = (
  config: InitImagineConfigConfig,
  options: InitImagineConfigOptions,
) => void;

export const initImagineConfig: InitImagineConfigFn = (
  config: InitImagineConfigConfig,
  options: InitImagineConfigOptions,
) => {
  let validatedConfig;
  try {
    validatedConfig = configSchema.parse(config);
  } catch (error) {
    throw new Error(
      "ImagineConfig initialization failed: " +
        (error instanceof Error ? error.message : String(error)),
    );
  }

  const rawCallbacks = options.callbacks ?? {};
  const wrappedCallbacks: ImagineCallbacks = {
    onProjectNameChange: wrapCallbackWithAnalytics(
      rawCallbacks.onProjectNameChange,
      "project.updated",
      (projectId, name) => (projectId ? { projectId, name } : null),
    ),
    onAddDomain: wrapCallbackWithAnalytics(
      rawCallbacks.onAddDomain,
      "project.domain_add_clicked",
      (projectId) => (projectId ? { projectId } : null),
    ),
    onManageDomains: wrapCallbackWithAnalytics(
      rawCallbacks.onManageDomains,
      "project.domain_manage_clicked",
      (projectId, primaryDomain) =>
        projectId ? { projectId, primaryDomain } : null,
    ),
    onUpgrade: wrapCallbackWithAnalytics(
      rawCallbacks.onUpgrade,
      "project.upgrade_clicked",
      (projectId) => ({ projectId: projectId ?? undefined }),
      false,
    ),
    onError: wrapCallbackWithAnalytics(
      rawCallbacks.onError,
      "studio.error",
      (projectId) => ({ projectId: projectId ?? undefined }),
    ),
  };

  baseStore.setState({
    config: validatedConfig,
    initialized: true,
    callbacks: wrappedCallbacks,
    consoleSessionId: options.consoleSessionId,
  });

  baseStore.getState().setTheme(options.initialTheme);
};

export const useImagineConfig = () => {
  const state = imagineConfigStore.getState().config;
  return state;
};

export const changeTheme = (theme: ImagineTheme) => {
  baseStore.getState().setTheme(theme);
};

export const useImagineTheme = () => {
  const imagineTheme = imagineConfigStore.use.theme();
  return imagineTheme;
};
